import React from 'react';
import { motion } from 'framer-motion';
export const DeliveryTruckIllustration: React.FC = () => {
  return <div className="relative w-full h-full bg-sky-soft overflow-hidden" data-id="element-129">
      {/* Sky/background gradient using flat colors */}
      <div className="absolute inset-0 bg-sky-soft" data-id="element-130" />

      {/* Sun/circle decoration */}
      <div className="absolute top-6 right-8 w-16 h-16 rounded-full bg-accent/40" data-id="element-131" />

      {/* Distant hills */}
      <svg viewBox="0 0 400 264" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" data-id="element-132">
        <path d="M 0 180 Q 80 140 160 165 T 320 155 T 400 170 L 400 264 L 0 264 Z" fill="#3aa856" opacity="0.25" data-id="element-133" />
        <path d="M 0 200 Q 100 175 200 195 T 400 195 L 400 264 L 0 264 Z" fill="#0f3d2e" opacity="0.18" data-id="element-134" />

        {/* Ground */}
        <rect x="0" y="220" width="400" height="44" fill="#0f3d2e" opacity="0.85" data-id="element-135" />

        {/* Road markings */}
        <g data-id="element-136">
          {[0, 1, 2, 3, 4, 5].map(i => <motion.rect key={i} x={i * 70} y="240" width="30" height="3" fill="#e0a73d" animate={{
          x: [i * 70, i * 70 - 70]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-137" />)}
        </g>
      </svg>

      {/* Truck — animated subtle bob */}
      <motion.svg viewBox="0 0 400 264" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" animate={{
      y: [0, -2, 0]
    }} transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-138">
        {/* Truck body group */}
        <g transform="translate(60, 110)" data-id="element-139">
          {/* Cargo box */}
          <rect x="0" y="0" width="180" height="100" fill="#faf8f3" stroke="#0f3d2e" strokeWidth="2" rx="4" data-id="element-140" />

          {/* Cargo box vertical seams */}
          <line x1="45" y1="0" x2="45" y2="100" stroke="#0f3d2e" strokeWidth="1" opacity="0.3" data-id="element-141" />
          <line x1="90" y1="0" x2="90" y2="100" stroke="#0f3d2e" strokeWidth="1" opacity="0.3" data-id="element-142" />
          <line x1="135" y1="0" x2="135" y2="100" stroke="#0f3d2e" strokeWidth="1" opacity="0.3" data-id="element-143" />

          {/* CycleBox logo on side */}
          <g transform="translate(50, 30)" data-id="element-144">
            <circle cx="10" cy="10" r="8" fill="none" stroke="#3aa856" strokeWidth="1.5" strokeDasharray="2 2" data-id="element-145" />
            <text x="24" y="14" fontFamily="Fraunces, serif" fontSize="14" fontWeight="600" fill="#0f3d2e" data-id="element-146">
              CycleBox
            </text>
          </g>

          {/* Open back doors revealing crates */}
          <g transform="translate(180, 0)" data-id="element-147">
            <rect x="0" y="0" width="40" height="100" fill="#faf8f3" stroke="#0f3d2e" strokeWidth="2" rx="2" data-id="element-148" />

            {/* Stacked crates visible through opening */}
            <g clipPath="url(#crate-clip)" data-id="element-149">
              <rect x="2" y="2" width="36" height="96" fill="#0f3d2e" data-id="element-150" />

              {/* Crate 1 - bottom row */}
              <CrateWithBottles x={4} y={60} bottleColor="#3aa856" data-id="element-151" />
              <CrateWithBottles x={4} y={30} bottleColor="#e0a73d" data-id="element-152" />
              <CrateWithBottles x={4} y={0} bottleColor="#1a3a2e" data-id="element-153" />
            </g>
            <defs data-id="element-154">
              <clipPath id="crate-clip" data-id="element-155">
                <rect x="2" y="2" width="36" height="96" rx="1" data-id="element-156" />
              </clipPath>
            </defs>
          </g>

          {/* Cab */}
          <g transform="translate(-50, 30)" data-id="element-157">
            <rect x="0" y="0" width="50" height="70" fill="#3aa856" stroke="#0f3d2e" strokeWidth="2" rx="6" data-id="element-158" />
            {/* Window */}
            <rect x="6" y="8" width="38" height="28" fill="#eaf5ec" stroke="#0f3d2e" strokeWidth="1.5" rx="2" data-id="element-159" />
            {/* Door line */}
            <line x1="6" y1="40" x2="44" y2="40" stroke="#0f3d2e" strokeWidth="1" opacity="0.4" data-id="element-160" />
            <circle cx="38" cy="48" r="1.5" fill="#0f3d2e" data-id="element-161" />
            {/* Headlight */}
            <rect x="-2" y="50" width="6" height="8" fill="#e0a73d" stroke="#0f3d2e" strokeWidth="1" rx="1" data-id="element-162" />
          </g>

          {/* Wheels */}
          <motion.g animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} style={{
          originX: '-25px',
          originY: '108px'
        }} data-id="element-163">
            <circle cx="-25" cy="108" r="14" fill="#1c1c1c" data-id="element-164" />
            <circle cx="-25" cy="108" r="6" fill="#8a8a82" data-id="element-165" />
            <line x1="-25" y1="102" x2="-25" y2="114" stroke="#1c1c1c" strokeWidth="2" data-id="element-166" />
            <line x1="-31" y1="108" x2="-19" y2="108" stroke="#1c1c1c" strokeWidth="2" data-id="element-167" />
          </motion.g>
          <motion.g animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} style={{
          originX: '60px',
          originY: '108px'
        }} data-id="element-168">
            <circle cx="60" cy="108" r="14" fill="#1c1c1c" data-id="element-169" />
            <circle cx="60" cy="108" r="6" fill="#8a8a82" data-id="element-170" />
            <line x1="60" y1="102" x2="60" y2="114" stroke="#1c1c1c" strokeWidth="2" data-id="element-171" />
            <line x1="54" y1="108" x2="66" y2="108" stroke="#1c1c1c" strokeWidth="2" data-id="element-172" />
          </motion.g>
          <motion.g animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} style={{
          originX: '140px',
          originY: '108px'
        }} data-id="element-173">
            <circle cx="140" cy="108" r="14" fill="#1c1c1c" data-id="element-174" />
            <circle cx="140" cy="108" r="6" fill="#8a8a82" data-id="element-175" />
            <line x1="140" y1="102" x2="140" y2="114" stroke="#1c1c1c" strokeWidth="2" data-id="element-176" />
            <line x1="134" y1="108" x2="146" y2="108" stroke="#1c1c1c" strokeWidth="2" data-id="element-177" />
          </motion.g>
        </g>
      </motion.svg>

      {/* Dust puffs behind the truck */}
      <motion.div className="absolute bottom-8 right-12 w-4 h-4 rounded-full bg-white/60" animate={{
      opacity: [0, 0.6, 0],
      scale: [0.5, 1.4, 0.5]
    }} transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeOut'
    }} data-id="element-178" />
      <motion.div className="absolute bottom-10 right-20 w-3 h-3 rounded-full bg-white/50" animate={{
      opacity: [0, 0.5, 0],
      scale: [0.5, 1.3, 0.5]
    }} transition={{
      duration: 1.5,
      repeat: Infinity,
      delay: 0.4,
      ease: 'easeOut'
    }} data-id="element-179" />
    </div>;
};
interface CrateProps {
  x: number;
  y: number;
  bottleColor: string;
}
const CrateWithBottles: React.FC<CrateProps> = ({
  x,
  y,
  bottleColor
}) => <g transform={`translate(${x}, ${y})`} data-id="element-180">
    {/* Crate */}
    <rect x="0" y="20" width="32" height="10" fill="#c97a4a" stroke="#0f3d2e" strokeWidth="0.5" data-id="element-181" />
    {/* Bottles peeking out */}
    {[0, 1, 2, 3].map(i => <g key={i} data-id="element-182">
        <rect x={3 + i * 7} y="6" width="5" height="16" fill={bottleColor} rx="0.5" data-id="element-183" />
        <rect x={4 + i * 7} y="3" width="3" height="4" fill={bottleColor} data-id="element-184" />
        <rect x={4 + i * 7} y="1" width="3" height="3" fill="#0f3d2e" data-id="element-185" />
      </g>)}
  </g>;